import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { memo, useCallback, useState } from "react";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import styles from "./AddLocation.module.css";
import ErrorAddOn from "../TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { ItemNoAndType } from "@coworker/apprestructured/src/shared/types/item";
import { GroupedSalesLocations } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import FixaTabs, {
  FixaTab,
  FixaTabPanel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import LocationsTab from "./LocationsTab/LocationsTab";
import { CustomLocationTab } from "./CustomLocationTab/CustomLocationTab";
import { LocationChange } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
interface AddLocationProps {
  onTabChanged: (tabId: AddLocationTab) => void;
  itemNoAndType: ItemNoAndType | null;
  slid: string;
  customLocation: string;
  disabled: boolean;
  onConfirmClick: ({ slid, customLocation }: LocationChange) => void;
  isLoadingLocations: boolean;
  filteredLocations: GroupedSalesLocations;
  totalAssignedSalesSpaceQuantity: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isError?: boolean;
}

export type AddLocationTab = "locations" | "customLocation";

export const DEFAULT_LOCATION_TAB: AddLocationTab = "locations";

export const AddLocation = memo(
  ({
    onTabChanged,
    itemNoAndType,
    slid,
    customLocation,
    disabled,
    onConfirmClick,
    isLoadingLocations,
    filteredLocations,
    totalAssignedSalesSpaceQuantity,
    searchQuery,
    setSearchQuery,
    isError = false,
  }: AddLocationProps) => {
    const [showModal, setShowModal] = useState(false);
    const [newSlid, setNewSlid] = useState<string>(slid);
    const [newCustomLocation, setNewCustomLocation] =
      useState<string>(customLocation);

    const handleOpenModal = useCallback(() => {
      setNewSlid(slid);
      setNewCustomLocation(customLocation);
      setSearchQuery("");
      onTabChanged(DEFAULT_LOCATION_TAB);
      setShowModal(true);
    }, [slid, customLocation, setSearchQuery, onTabChanged]);

    const {
      saveGuardedFunction: handleCloseModal,
      enableSaveGuard,
      disableSaveGuard,
    } = useSaveGuardedFunction(
      useCallback(() => {
        setShowModal(false);
      }, [])
    );

    const handleConfirm = useCallback(() => {
      onConfirmClick({
        slid: newSlid,
        customLocation: newCustomLocation,
      });
      setShowModal(false);
      disableSaveGuard();
    }, [onConfirmClick, newSlid, newCustomLocation, disableSaveGuard]);

    const handleSlidChange = useCallback(
      (nextSlid: string) => {
        setNewSlid(nextSlid);
        setNewCustomLocation(""); //Clear the custom location when slid is being edited to avoid confusion, as only one of them can be set.
        enableSaveGuard();
      },
      [enableSaveGuard]
    );

    const handleCustomLocationChange = useCallback(
      (newCustomLocation: string) => {
        setNewCustomLocation(newCustomLocation);
        setNewSlid(""); //Clear the slid when custom location is being edited to avoid confusion, as only one of them can be set.
        enableSaveGuard();
      },
      [enableSaveGuard]
    );

    const { t } = useTranslation();

    const handleTabChange = useCallback(
      (tabPanelId: string) => {
        const tab = tabPanelId as AddLocationTab;
        onTabChanged(tab);
      },
      [onTabChanged]
    );

    const description = slid || customLocation;
    const emphasised = !!slid || !!customLocation;

    return (
      <>
        <FixaListViewItem
          data-testid="add-location-list-item"
          title={t("location3String")}
          inset
          onClick={handleOpenModal}
          emphasised={emphasised}
          description={description}
          control="navigational"
          controlIcon={null}
          disabled={disabled}
          addOn={
            isError ? (
              <ErrorAddOn testId="add-location-error">
                {t("pleaseFillInString")}
              </ErrorAddOn>
            ) : null
          }
        />
        <FixaModal
          visible={showModal}
          handleCloseBtn={handleCloseModal}
          data-testid="add-location-modal"
        >
          <FixaSheets
            labelledById="task-management-add-location-header"
            header={
              <FixaModalHeader
                id="task-management-add-location-header"
                title={t("location3String")}
                closeBtnClick={handleCloseModal}
              />
            }
            footer={
              <FixaModalFooter renderBorder>
                <FixaButton
                  data-testid="add-location-confirm"
                  type="primary"
                  fluid
                  text={t("confirmString")}
                  onClick={handleConfirm}
                />
              </FixaModalFooter>
            }
            fullSize
          >
            <FixaModalBody>
              <FixaTabs
                className={styles["root"]!}
                defaultActiveTab={DEFAULT_LOCATION_TAB}
                tabs={[
                  <FixaTab
                    data-testid="add-location-locations-tab"
                    key="locations"
                    tabPanelId="locations"
                    text={t("locationsString")}
                  />,
                  <FixaTab
                    data-testid="add-location-custom-location-tab"
                    key="customLocation"
                    tabPanelId="customLocation"
                    text={t("customLocationString")}
                  />,
                ]}
                onTabChanged={handleTabChange}
                tabPanels={[
                  <FixaTabPanel
                    data-testid="add-location-locations-tab-panel"
                    key="locations-tab-panel"
                    tabPanelId="locations"
                  >
                    <LocationsTab
                      onSlidChange={handleSlidChange}
                      itemNoAndType={itemNoAndType}
                      selectedSlid={newSlid}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      isLoadingLocations={isLoadingLocations}
                      filteredLocations={filteredLocations}
                      totalAssignedSalesSpaceQuantity={
                        totalAssignedSalesSpaceQuantity
                      }
                    />
                  </FixaTabPanel>,
                  <FixaTabPanel
                    data-testid="add-location-custom-location-tab-panel"
                    key="customLocation-tab-panel"
                    tabPanelId="customLocation"
                  >
                    <CustomLocationTab
                      customLocation={newCustomLocation}
                      onCustomLocationChange={handleCustomLocationChange}
                    />
                  </FixaTabPanel>,
                ]}
              />
            </FixaModalBody>
          </FixaSheets>
        </FixaModal>
      </>
    );
  }
);
